import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VOICE_START_UNAVAILABLE_EVENT,
  type VoiceStartUnavailableReason,
} from '@/lib/voice-start-unavailable-events';
import styles from './VoiceStartUnavailableToast.module.css';

const MESSAGE_KEY: Record<VoiceStartUnavailableReason, string> = {
  'retry-soon': 'voice.startUnavailableRetrySoon',
  offline: 'voice.startUnavailableOffline',
  unavailable: 'voice.startUnavailable',
};

/** Longer than the 5 s of the shorter voice toasts: two sentences to read. */
const VISIBLE_MS = 7000;

interface Props {
  /** Called when the toast opens; the provider clears a stale error toast at the same spot. */
  onShow?: () => void;
}

/**
 * Bottom-anchored toast for a mic tap that could start no transcription
 * (rule #21: the microphone never silently does nothing). It says why and
 * what to do next: type, and, only when a temporary fallback is the reason,
 * when the microphone will work again. Tap to dismiss; auto-dismiss after
 * VISIBLE_MS; every refused tap shows it again.
 *
 * Triggered by the `adia:voice-start-unavailable` window event from
 * useSpeechToText (rule #26). Mounted globally inside VoiceCaptureProvider so
 * every voice-capture surface shares one mount. The live region is always
 * mounted (role status, aria-live assertive, as VoiceErrorToast: the person
 * needs to know recording did not start) so the inserted message is
 * announced; a repeated refusal re-inserts it.
 */
export default function VoiceStartUnavailableToast({ onShow }: Props) {
  const { t } = useTranslation();
  const [notice, setNotice] = useState<{ id: number; reason: VoiceStartUnavailableReason } | null>(null);
  const onShowRef = useRef(onShow);
  useEffect(() => {
    onShowRef.current = onShow;
  }, [onShow]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let seq = 0;
    const onUnavailable = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      const reason: VoiceStartUnavailableReason =
        detail === 'retry-soon' || detail === 'offline' ? detail : 'unavailable';
      seq += 1;
      setNotice({ id: seq, reason });
      try {
        onShowRef.current?.();
      } catch {
        // Presentation only.
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setNotice(null);
        timer = null;
      }, VISIBLE_MS);
    };
    window.addEventListener(VOICE_START_UNAVAILABLE_EVENT, onUnavailable);
    return () => {
      window.removeEventListener(VOICE_START_UNAVAILABLE_EVENT, onUnavailable);
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <div className={styles.region} role="status" aria-live="assertive" aria-atomic="true">
      {notice && (
        <button
          key={notice.id}
          type="button"
          className={styles.toast}
          onClick={() => setNotice(null)}
        >
          <span className={styles.icon} aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </span>
          <span className={styles.message}>{t(MESSAGE_KEY[notice.reason])}</span>
        </button>
      )}
    </div>
  );
}
